import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

const COMPLEXITY_BADGE = {
  ordinary: { bg: 'bg-slate-100 border-slate-200 text-slate-700', label: 'Standard' },
  expert: { bg: 'bg-indigo-50 border-indigo-200 text-indigo-700', label: 'Expert' },
  super_expertise: { bg: 'bg-amber-50 border-amber-200 text-amber-800', label: 'Super Expertise' },
};

export default function ProceduresTab() {
  const { user } = useAuth();
  const [procedures, setProcedures] = useState([]);
  const [showCatalog, setShowCatalog] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', type: 'OPD Consultation', duration_minutes: 30, price_min: '', price_max: '' });
  const [saving, setSaving] = useState(false);

  // Catalog state
  const [catalog, setCatalog] = useState([]);
  const [catFilter, setCatFilter] = useState('');
  const [catComplexity, setCatComplexity] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [addingCatalog, setAddingCatalog] = useState(false);

  // Blend state
  const [blendingId, setBlendingId] = useState(null);
  const [blendNote, setBlendNote] = useState('');
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [priceForm, setPriceForm] = useState({ price_min: '', price_max: '' });
  const [savingPrice, setSavingPrice] = useState(false);
  const aiQuota = useAiQuota();

  const openPriceEditor = (p) => {
    setEditingPriceId(editingPriceId === p.id ? null : p.id);
    setPriceForm({
      price_min: p.price_min != null ? String(p.price_min) : '',
      price_max: p.price_max != null ? String(p.price_max) : '',
    });
  };

  const handleSavePrice = async (procId) => {
    const pMin = priceForm.price_min === '' ? null : parseInt(priceForm.price_min, 10);
    const pMax = priceForm.price_max === '' ? null : parseInt(priceForm.price_max, 10);
    if (pMin != null && pMax != null && pMax < pMin) {
      alert('Max price must be ≥ Min price.');
      return;
    }
    setSavingPrice(true);
    try {
      // Find the existing procedure so we can preserve its other fields in the PUT payload
      const existing = procedures.find(p => p.id === procId);
      if (!existing) return;
      await api.put(`/dashboard/procedures/${procId}`, {
        name: existing.name,
        description: existing.description || '',
        type: existing.type,
        duration_minutes: existing.duration_minutes,
        indications: existing.indications || '',
        price_min: pMin,
        price_max: pMax,
      });
      setEditingPriceId(null);
      load();
    } catch (e) {
      alert('Failed to save price. Please try again.');
    } finally {
      setSavingPrice(false);
    }
  };
  const [blending, setBlending] = useState(false);

  // Personalize editor (template 20% diff guard) ---------------------------
  const [editingProcId, setEditingProcId] = useState(null);
  const [editDesc, setEditDesc] = useState('');
  const [editPriceMin, setEditPriceMin] = useState('');
  const [editPriceMax, setEditPriceMax] = useState('');
  const [editDuration, setEditDuration] = useState(30);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editErr, setEditErr] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState('');

  const openCustomize = (p) => {
    setEditingProcId(p.id);
    setEditDesc(p.description || '');
    setEditPriceMin(p.price_min != null ? String(p.price_min) : '');
    setEditPriceMax(p.price_max != null ? String(p.price_max) : '');
    setEditDuration(p.duration_minutes || 30);
    setEditErr('');
  };

  const tokenize = (s) => (s || '').toLowerCase().match(/[a-z0-9']+/g) || [];
  const wordDiffRatio = (orig, nxt) => {
    const o = tokenize(orig); const n = tokenize(nxt);
    if (!o.length && !n.length) return 0;
    if (!o.length || !n.length) return 1;
    const co = new Map(); const cn = new Map();
    o.forEach(w => co.set(w, (co.get(w) || 0) + 1));
    n.forEach(w => cn.set(w, (cn.get(w) || 0) + 1));
    let removed = 0, added = 0;
    co.forEach((v, k) => { removed += Math.max(0, v - (cn.get(k) || 0)); });
    cn.forEach((v, k) => { added += Math.max(0, v - (co.get(k) || 0)); });
    return (removed + added) / (2 * Math.max(o.length, n.length));
  };

  const handleSaveCustomize = async (p) => {
    setSavingEdit(true); setEditErr('');
    try {
      const pMin = editPriceMin === '' ? null : parseInt(editPriceMin, 10);
      const pMax = editPriceMax === '' ? null : parseInt(editPriceMax, 10);
      await api.put(`/dashboard/procedures/${p.id}`, {
        name: p.name,
        description: editDesc,
        type: p.type,
        duration_minutes: parseInt(editDuration, 10) || 30,
        indications: p.indications || '',
        price_min: pMin,
        price_max: pMax,
      });
      setEditingProcId(null);
      load();
    } catch (e) {
      setEditErr(formatApiError(e.response?.data?.detail) || 'Failed to save');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAutoSeed = async () => {
    setSeeding(true); setSeedMsg('');
    try {
      const { data } = await api.post('/dashboard/procedures/auto-seed');
      setSeedMsg(`Added ${data.seeded_total || 0} procedures from the DocSite AI master catalog.`);
      load();
      setTimeout(() => setSeedMsg(''), 5000);
    } catch (e) {
      setSeedMsg('Error: ' + (formatApiError(e.response?.data?.detail) || 'Could not seed catalog'));
    } finally {
      setSeeding(false);
    }
  };

  const [profile, setProfile] = useState(null);
  const isPremium = profile?.tier === 'premium' || profile?.tier === 'turbo';
  const load = useCallback(() => {
    api.get('/dashboard/procedures').then(r => setProcedures(r.data)).catch(() => {});
    api.get('/dashboard/profile').then(r => setProfile(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadCatalog = (spec) => {
    const params = {};
    if (spec) params.specialization = spec;
    api.get('/procedure-catalog', { params }).then(r => setCatalog(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (showCatalog) loadCatalog(catFilter);
  }, [showCatalog, catFilter]);

  const toggleSelect = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleAddFromCatalog = async () => {
    if (selected.size === 0) return;
    setAddingCatalog(true);
    try {
      await api.post('/dashboard/procedures/from-catalog', { catalog_names: [...selected] });
      setSelected(new Set());
      setShowCatalog(false);
      load();
    } catch {} finally { setAddingCatalog(false); }
  };

  const handleManualAdd = async () => {
    setSaving(true);
    try {
      const desc = isPremium ? form.description : form.description.split('\n').slice(0, 5).join('\n');
      const pMin = form.price_min === '' ? null : parseInt(form.price_min, 10);
      const pMax = form.price_max === '' ? null : parseInt(form.price_max, 10);
      if (pMin != null && pMax != null && pMax < pMin) {
        alert('Max price must be greater than or equal to Min price.');
        setSaving(false);
        return;
      }
      await api.post('/dashboard/procedures', {
        ...form,
        description: desc,
        price_min: pMin,
        price_max: pMax,
      });
      setForm({ name: '', description: '', type: 'OPD Consultation', duration_minutes: 30, price_min: '', price_max: '' });
      setShowManual(false);
      load();
    } catch {} finally { setSaving(false); }
  };

  const handleBlend = async (procId) => {
    if (!blendNote.trim()) return;
    if (!aiQuota.eligible) { alert('AI blend is a TurboCharge-only feature.'); return; }
    if (aiQuota.remaining <= 0) { alert(`Monthly AI pull quota reached (${aiQuota.used}/${aiQuota.limit}). Resets on the 1st.`); return; }
    setBlending(true);
    try {
      const { data } = await api.post(`/dashboard/procedures/${procId}/blend`, { custom_note: blendNote });
      setProcedures(prev => prev.map(p => p.id === procId ? { ...p, blended_description: data.blended_description, custom_note: data.custom_note } : p));
      setBlendingId(null);
      setBlendNote('');
      aiQuota.refresh();
    } catch (e) { alert(formatApiError(e)); } finally { setBlending(false); }
  };

  const handleDelete = async (id) => {
    await api.delete(`/dashboard/procedures/${id}`);
    load();
  };

  const filteredCatalog = catComplexity ? catalog.filter(c => c.complexity === catComplexity) : catalog;
  const existingNames = new Set(procedures.map(p => p.name));

  // Group catalog by complexity
  const grouped = { ordinary: [], expert: [], super_expertise: [] };
  filteredCatalog.forEach(c => {
    if (!existingNames.has(c.name)) {
      (grouped[c.complexity] || grouped.ordinary).push(c);
    }
  });

  return (
    <div className="space-y-4" data-testid="procedures-tab">
      {/* Voice meter for the procedures section narrative — required to publish */}
      <VoiceAuthenticityNotice />
      <VoiceMeter section="procedures" heading="Procedure descriptions — in your voice" />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Procedures & Treatments</h3>
          <p className="text-xs text-slate-500">{isPremium ? 'Premium: Auto-populate from catalog + AI personalization' : 'Add procedures you offer to your patients'}</p>
        </div>
        <div className="flex gap-2">
          {isPremium && (
            <button onClick={() => { setShowCatalog(!showCatalog); setShowManual(false); }}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${showCatalog ? 'bg-amber-500 text-white' : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'}`}
              data-testid="open-catalog-button">
              <Star size={14} /> {showCatalog ? 'Close Catalog' : 'Add from Catalog'}
            </button>
          )}
          <button onClick={() => { setShowManual(!showManual); setShowCatalog(false); }}
            className="flex items-center gap-1.5 rounded-full px-4 py-2 bg-sky-600 text-white text-sm font-medium hover:bg-sky-700"
            data-testid="add-manual-proc-button">
            <Plus size={14} /> Add Custom
          </button>
        </div>
      </div>

      {/* Master-catalog auto-seed banner — the "brand promise" surface */}
      {(() => {
        const specs = profile?.specializations || [];
        const hasEnt = specs.includes('ent') || specs.includes('otolaryngology');
        const hasTemplates = procedures.some(p => p.source === 'master_catalog' || p.is_template);
        if (!hasEnt || hasTemplates) return null;
        return (
          <div className="bg-gradient-to-r from-indigo-50 via-sky-50 to-emerald-50 border border-indigo-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="master-catalog-banner">
            <div className="w-10 h-10 rounded-xl bg-white border border-indigo-200 flex items-center justify-center shrink-0">
              <Sparkles size={18} className="text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Your DocSite AI Procedure Catalog is ready</p>
              <p className="text-xs text-slate-600 mt-0.5">Load the full ENT master catalog (~40 procedures, pre-drafted descriptions, typical price ranges). You then delete what you don't do, personalize each description and set your own prices.</p>
            </div>
            <button onClick={handleAutoSeed} disabled={seeding}
              className="shrink-0 rounded-full px-4 py-2 bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="seed-master-catalog-btn">
              {seeding ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {seeding ? 'Loading…' : 'Load My Catalog'}
            </button>
          </div>
        );
      })()}
      {seedMsg && (
        <div className={`text-xs font-medium ${seedMsg.startsWith('Error') ? 'text-rose-600' : 'text-emerald-600'}`} data-testid="seed-msg">
          {seedMsg}
        </div>
      )}

      {/* Premium Catalog Grid */}
      {showCatalog && isPremium && (
        <div className="bg-gradient-to-br from-amber-50/50 to-orange-50/50 rounded-2xl border-2 border-amber-200 p-5 space-y-4" data-testid="catalog-grid">
          <div className="flex items-center gap-2 mb-2">
            <Star size={16} className="text-amber-500" />
            <span className="text-sm font-semibold text-amber-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Premium Procedure Catalog</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <select className="rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm outline-none" value={catFilter} onChange={e => setCatFilter(e.target.value)} data-testid="catalog-spec-filter">
              <option value="">All Specializations</option>
              {['cardiology','orthopedics','ent','dermatology','ophthalmology','general_surgery'].map(s => (
                <option key={s} value={s} className="capitalize">{s.replace('_',' ')}</option>
              ))}
            </select>
            <div className="flex gap-1">
              {[{k:'',l:'All'},{k:'ordinary',l:'Standard'},{k:'expert',l:'Expert'},{k:'super_expertise',l:'Super'}].map(f => (
                <button key={f.k} onClick={() => setCatComplexity(f.k)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${catComplexity === f.k ? 'bg-amber-500 text-white' : 'bg-white text-amber-700 border border-amber-200 hover:bg-amber-50'}`}
                  data-testid={`cat-complexity-${f.k || 'all'}`}>
                  {f.l}
                </button>
              ))}
            </div>
          </div>

          {/* Grouped Grid */}
          {['ordinary', 'expert', 'super_expertise'].map(complexity => {
            const items = grouped[complexity];
            if (items.length === 0) return null;
            const badge = COMPLEXITY_BADGE[complexity];
            return (
              <div key={complexity}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${badge.bg}`}>{badge.label}</span>
                  <span className="text-[10px] text-slate-400">{items.length} procedures</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {items.map(c => {
                    const isSel = selected.has(c.name);
                    return (
                      <button key={c.name} onClick={() => toggleSelect(c.name)}
                        className={`text-left p-3 rounded-xl border-2 transition-all ${isSel ? 'border-amber-400 bg-amber-50 shadow-sm' : 'border-slate-200 bg-white hover:border-amber-200'}`}
                        data-testid={`catalog-${c.name.replace(/\s+/g, '-').toLowerCase()}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-slate-900 truncate">{c.name}</p>
                            <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{c.description}</p>
                          </div>
                          <div className={`w-5 h-5 rounded-md border-2 flex-shrink-0 flex items-center justify-center ${isSel ? 'bg-amber-500 border-amber-500' : 'border-slate-300'}`}>
                            {isSel && <CheckCircle size={12} className="text-white" />}
                          </div>
                        </div>
                        <div className="flex gap-1.5 mt-2">
                          <span className={`px-1.5 py-0.5 rounded text-[8px] font-medium ${c.type === 'Major Surgery' ? 'bg-red-50 text-red-600' : c.type === 'Minor Procedure' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>{c.type}</span>
                          <span className="text-[8px] text-slate-400">{c.duration_minutes}min</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {selected.size > 0 && (
            <button onClick={handleAddFromCatalog} disabled={addingCatalog}
              className="w-full rounded-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50"
              data-testid="add-selected-catalog">
              {addingCatalog ? 'Adding...' : `Add ${selected.size} Selected Procedure${selected.size > 1 ? 's' : ''} to My Website`}
            </button>
          )}
        </div>
      )}

      {/* Manual Add Form */}
      {showManual && (
        <div className="bg-white rounded-2xl border border-sky-200 p-5 space-y-3" data-testid="manual-procedure-form">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} testid="proc-name" />
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Type</label>
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} data-testid="proc-type">
                <option>OPD Consultation</option><option>Minor Procedure</option><option>Major Surgery</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">
              Description {!isPremium && <span className="text-slate-400">(max 5 lines)</span>}
            </label>
            <textarea className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none resize-none"
              rows={isPremium ? 3 : 5} maxLength={isPremium ? undefined : 500}
              value={form.description} onChange={e => {
                const val = isPremium ? e.target.value : e.target.value.split('\n').slice(0, 5).join('\n');
                setForm(f => ({ ...f, description: val }));
              }} data-testid="proc-desc" placeholder={isPremium ? 'Describe this procedure...' : 'Describe this procedure (max 5 lines)'} />
          </div>
          <button onClick={handleManualAdd} disabled={saving || !form.name} className="rounded-full px-5 py-2 bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50" data-testid="save-proc-button">
            {saving ? 'Saving...' : 'Save Procedure'}
          </button>
        </div>
      )}

      {/* Procedure List */}
      {procedures.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
          <Stethoscope size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No procedures yet.</p>
          {isPremium && <p className="text-xs text-amber-600 mt-1">Use the premium catalog to instantly populate your website!</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {procedures.map(p => {
            const badge = COMPLEXITY_BADGE[p.complexity] || COMPLEXITY_BADGE.ordinary;
            const isBlendOpen = blendingId === p.id;
            return (
              <div key={p.id} className={`bg-white rounded-2xl border ${p.is_template && !p.personalized ? 'border-indigo-200 ring-1 ring-indigo-100' : p.source === 'catalog' ? 'border-amber-100' : 'border-slate-200'} p-4`} data-testid={`proc-item-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-medium text-slate-900">{p.name}</h4>
                      {p.source === 'catalog' && <Star size={10} className="text-amber-500" />}
                      {p.complexity && <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold border ${badge.bg}`}>{badge.label}</span>}
                      {p.is_template && !p.personalized && (
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200 flex items-center gap-1" data-testid={`template-badge-${p.id}`}>
                          <Sparkles size={9} /> AI TEMPLATE — CUSTOMIZE
                        </span>
                      )}
                      {p.is_template && p.personalized && (
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                          <CheckCircle size={9} /> PERSONALIZED
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{p.blended_description || p.description}</p>
                    {p.custom_note && <p className="text-[10px] text-amber-600 mt-1 italic">Your note: "{p.custom_note}"</p>}
                    <div className="flex gap-2 mt-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${p.type === 'Major Surgery' ? 'bg-red-50 text-red-700' : p.type === 'Minor Procedure' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>{p.type}</span>
                      <span className="text-[10px] text-slate-600 font-medium flex items-center gap-0.5"><Clock size={10} /> {p.duration_minutes}m</span>
                      {(p.price_min != null || p.price_max != null) && (
                        <span className="text-[10px] text-slate-600 font-medium" data-testid={`proc-price-dash-${p.id}`}>
                          {p.price_min != null && p.price_max != null && p.price_min !== p.price_max
                            ? `₹${Number(p.price_min).toLocaleString('en-IN')}–₹${Number(p.price_max).toLocaleString('en-IN')}`
                            : `₹${Number(p.price_max ?? p.price_min).toLocaleString('en-IN')}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => editingProcId === p.id ? setEditingProcId(null) : openCustomize(p)}
                      className={`p-1.5 rounded-lg text-xs ${editingProcId === p.id ? 'bg-indigo-100 text-indigo-700' : p.is_template && !p.personalized ? 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
                      title="Customize description, price & time" data-testid={`customize-btn-${p.id}`}>
                      <Edit3 size={14} />
                    </button>
                    <button onClick={() => openPriceEditor(p)}
                      className={`p-1.5 rounded-lg text-xs ${editingPriceId === p.id ? 'bg-sky-100 text-sky-700' : 'bg-sky-50 text-sky-600 hover:bg-sky-100'}`}
                      title="Set price range" data-testid={`edit-price-btn-${p.id}`}>
                      ₹
                    </button>
                    {isPremium && p.source === 'catalog' && (
                      <button onClick={() => { setBlendingId(isBlendOpen ? null : p.id); setBlendNote(p.custom_note || ''); }}
                        className={`p-1.5 rounded-lg text-xs ${isBlendOpen ? 'bg-amber-100 text-amber-700' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'}`}
                        title="AI Personalize" data-testid={`blend-btn-${p.id}`}>
                        <Sparkles size={14} />
                      </button>
                    )}
                    <button onClick={() => handleDelete(p.id)} className="text-red-400 hover:text-red-600 p-1.5" data-testid={`del-proc-${p.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>

                {/* Customize / Personalize Editor */}
                {editingProcId === p.id && (() => {
                  const orig = p.original_description || p.description || '';
                  const ratio = p.is_template && !p.personalized ? wordDiffRatio(orig, editDesc) : 1;
                  const pct = Math.round(ratio * 100);
                  const ok = ratio >= 0.20 || !(p.is_template && !p.personalized);
                  return (
                    <div className="mt-4 pt-4 border-t border-indigo-100 space-y-3" data-testid={`customize-panel-${p.id}`}>
                      {p.is_template && !p.personalized && (
                        <div className="bg-indigo-50/70 border border-indigo-200 rounded-xl p-3">
                          <p className="text-[11px] text-indigo-800 font-medium">This is the DocSite AI template for <b>{p.name}</b>. Please rewrite it in your own voice — mention your clinic, your technique, your experience, the language you speak with patients. You need to change <b>at least 20%</b> of the words before saving.</p>
                        </div>
                      )}
                      <div>
                        <label className="text-[11px] font-medium text-slate-600 mb-1 block">Description</label>
                        <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={5}
                          className="w-full rounded-xl border border-indigo-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
                          data-testid={`customize-desc-${p.id}`} />
                        {p.is_template && !p.personalized && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-[10px] font-medium mb-1">
                              <span className={ok ? 'text-emerald-700' : 'text-indigo-700'} data-testid={`diff-pct-${p.id}`}>
                                {ok ? '✓ Ready to save' : `Keep editing — ${pct}% changed`}
                              </span>
                              <span className="text-slate-400">target: 20%</span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${ok ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${Math.min(100, pct * 5)}%` }} />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-[11px] font-medium text-slate-600 mb-1 block">Duration (min)</label>
                          <input type="number" min="5" max="480" value={editDuration}
                            onChange={e => setEditDuration(e.target.value.replace(/[^0-9]/g, ''))}
                            className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs outline-none"
                            data-testid={`customize-duration-${p.id}`} />
                        </div>
                        <div>
                          <label className="text-[11px] font-medium text-slate-600 mb-1 block">Price min ₹</label>
                          <input type="number" value={editPriceMin}
                            onChange={e => setEditPriceMin(e.target.value.replace(/[^0-9]/g, ''))}
                            className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs outline-none"
                            data-testid={`customize-pmin-${p.id}`} />
                        </div>
                        <div>
                          <label className="text-[11px] font-medium text-slate-600 mb-1 block">Price max ₹</label>
                          <input type="number" value={editPriceMax}
                            onChange={e => setEditPriceMax(e.target.value.replace(/[^0-9]/g, ''))}
                            className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs outline-none"
                            data-testid={`customize-pmax-${p.id}`} />
                        </div>
                      </div>
                      {editErr && <p className="text-xs text-rose-600 font-medium" data-testid={`customize-err-${p.id}`}>{editErr}</p>}
                      <div className="flex gap-2">
                        <button onClick={() => handleSaveCustomize(p)} disabled={savingEdit || !ok}
                          className="rounded-full px-5 py-1.5 bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                          data-testid={`customize-save-${p.id}`}>
                          {savingEdit ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                          {ok ? 'Save' : `Need ${20 - pct}% more edits`}
                        </button>
                        <button onClick={() => setEditingProcId(null)}
                          className="rounded-full px-4 py-1.5 text-xs text-slate-500 hover:text-slate-700"
                          data-testid={`customize-cancel-${p.id}`}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Price Editor Panel */}
                {editingPriceId === p.id && (
                  <div className="mt-3 pt-3 border-t border-sky-100 space-y-2" data-testid={`price-panel-${p.id}`}>
                    <p className="text-[10px] font-medium text-sky-700">Average Price Range (optional, ₹). Shown on your public website.</p>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" placeholder="Min ₹"
                        value={priceForm.price_min}
                        onChange={e => setPriceForm(f => ({ ...f, price_min: e.target.value.replace(/[^0-9]/g, '') }))}
                        className="rounded-lg border border-sky-200 px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-sky-200"
                        data-testid={`price-min-input-${p.id}`} />
                      <input type="number" placeholder="Max ₹"
                        value={priceForm.price_max}
                        onChange={e => setPriceForm(f => ({ ...f, price_max: e.target.value.replace(/[^0-9]/g, '') }))}
                        className="rounded-lg border border-sky-200 px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-sky-200"
                        data-testid={`price-max-input-${p.id}`} />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleSavePrice(p.id)} disabled={savingPrice}
                        className="rounded-full px-4 py-1.5 bg-sky-600 text-white text-xs font-medium hover:bg-sky-700 disabled:opacity-50"
                        data-testid={`price-save-${p.id}`}>
                        {savingPrice ? 'Saving…' : 'Save price'}
                      </button>
                      <button onClick={() => setEditingPriceId(null)} className="rounded-full px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700" data-testid={`price-cancel-${p.id}`}>
                        Cancel
                      </button>
                      {(p.price_min != null || p.price_max != null) && (
                        <button onClick={() => { setPriceForm({ price_min: '', price_max: '' }); }} className="rounded-full px-3 py-1.5 text-xs text-red-500 hover:text-red-700" data-testid={`price-clear-${p.id}`}>
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {/* AI Blend Panel */}
                {isBlendOpen && isPremium && (
                  <div className="mt-3 pt-3 border-t border-amber-100 space-y-2" data-testid={`blend-panel-${p.id}`}>
                    <p className="text-[10px] font-medium text-amber-700">Add your personal touch (2 sentences max). AI will blend it seamlessly.</p>
                    <textarea className="w-full rounded-xl border border-amber-200 bg-amber-50/50 px-3 py-2 text-sm outline-none resize-none"
                      rows={2} maxLength={300} value={blendNote} onChange={e => setBlendNote(e.target.value)}
                      placeholder='e.g. "I have performed over 3000 of these procedures. I use the latest 4th-gen technology."'
                      data-testid={`blend-input-${p.id}`} />
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleBlend(p.id)} disabled={blending || !blendNote.trim()}
                        className="rounded-full px-4 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 flex items-center gap-1"
                        data-testid={`blend-submit-${p.id}`}>
                        {blending ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                        {blending ? 'Blending...' : 'AI Blend'}
                      </button>
                      <span className="text-[10px] text-slate-400">{blendNote.length}/300</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

